import { createSlice } from "@reduxjs/toolkit";
import { ValidateReturnedPatches } from "../../apis/dispense/validateReturnPatches";

const initialState = {
    loading: false,
    data: null,
    error: null,
    previousPatchIds: [],
    isReturn: {},
    winrx_data_id: []
}

const ValidateReturnedPatchesSlice = createSlice({
    name: 'ValidateReturnedPatches',
    initialState,

    reducers: {
        RemoveAllPatchesId(state) {
            state.previousPatchIds = []
            state.data = null
            state.isReturn = {}
            state.winrx_data_id = []
        },
        // How many used patches came back, typed by the driver after "Yes", and the
        // most that can come back (patches handed out last time), for the check on confirm.
        // latestAnswer: the Yes / No / N/A just tapped, so a slower reply to an earlier tap is ignored.
        SetReturnedPatchCount(state, action) {
            const { winrxDataId, count, expected, latestAnswer } = action.payload
            state.isReturn[winrxDataId] = {
                ...(state.isReturn[winrxDataId] || {}),
                returned_qty: count,
                expected_qty: expected ?? null,
                ...(latestAnswer ? { latest_answer: latestAnswer } : {}),
            }
        },
        // Patches still owed from earlier visits: 'all', 'other' (+ typed count) or
        // 'none'. late_qty is what is sent: all = everything owed, none = 0.
        SetLatePatchReturn(state, action) {
            const { winrxDataId, mode, qty, owed } = action.payload
            const lateQty = mode === 'all' ? String(owed) : (mode === 'none' ? '0' : String(qty ?? ''))
            state.isReturn[winrxDataId] = { ...(state.isReturn[winrxDataId] || {}), late_mode: mode, late_qty: lateQty, late_owed: owed }
        }
    },

    extraReducers: (builder) => {
        builder.addCase(ValidateReturnedPatches.pending.type, (state) => {
            return {
                ...state,
                loading: true,
                data: null,
                error: null
            }
        })

        builder.addCase(ValidateReturnedPatches.fulfilled.type, (state, action) => {

            const { previous_patch_id, winrx_data_id, is_return } = action?.payload?.data;

            // The driver has already changed the answer: this reply is out of date, and
            // applying it would wipe the count or put the old answer back.
            const latestAnswer = state.isReturn?.[winrx_data_id]?.latest_answer ?? is_return;
            if (is_return !== latestAnswer) {
                return { ...state, loading: false, data: action.payload, error: null };
            }

            return {
                ...state,
                loading: false,
                data: action.payload,
                error: null,
                previousPatchIds: state?.previousPatchIds?.includes(previous_patch_id)
                    ? state?.previousPatchIds
                    : [...state.previousPatchIds, previous_patch_id],
                isReturn: {
                    ...state.isReturn,
                    [winrx_data_id]: {
                        previous_patch_id: previous_patch_id,
                        answer: is_return,
                        latest_answer: latestAnswer,
                        // The count only belongs to a "Yes"; it is kept if typed before this returned.
                        returned_qty: is_return === 'yes' ? (state.isReturn?.[winrx_data_id]?.returned_qty ?? '') : undefined,
                        expected_qty: state.isReturn?.[winrx_data_id]?.expected_qty ?? null,
                        // Still-owed answer is independent of Yes / No / N/A.
                        late_mode: state.isReturn?.[winrx_data_id]?.late_mode ?? '',
                        late_qty: state.isReturn?.[winrx_data_id]?.late_qty ?? '',
                        late_owed: state.isReturn?.[winrx_data_id]?.late_owed ?? 0
                    }
                },
                winrx_data_id: state?.winrx_data_id?.includes(winrx_data_id)
                    ? state?.winrx_data_id
                    : [...state.winrx_data_id, winrx_data_id],
            };

        })

        builder.addCase(ValidateReturnedPatches.rejected.type, (state, action) => {
            return {
                ...state,
                loading: false,
                data: null,
                error: action.payload
            }
        })
    }

})

export const { RemoveAllPatchesId, SetReturnedPatchCount, SetLatePatchReturn } = ValidateReturnedPatchesSlice.actions;

export default ValidateReturnedPatchesSlice.reducer